'use client';

import * as React from 'react';
import { styled } from '@linaria/react';

import { BREAKPOINTS } from '@/constants';
import {
  LIGHT_COLORS_RAW,
  DARK_COLORS_RAW,
  vals,
} from '@/constants/colors';
import {
  useNewsletterSignup,
  useConfetti,
} from '@/helpers/newsletter.helpers';

import TextInput from '@/components/TextInput';
import Spinner from '@/components/Spinner';
import Paragraph from '@/components/Paragraph';
import Spicy from '@/components/Spicy';
import SubscribeError from '@/components/SubscribeError';
import ButtonRainbow from '@/components/ButtonRainbow';
import Sparkles from '@/components/Sparkles';
import SubscribeGeyser from '@/components/SubscribeGeyser';

function ContentNewsletterSignup() {
  const {
    confettiStatus,
    handleLoadConfetti,
    handleTriggerConfetti,
  } = useConfetti();

  const {
    status,
    emailFields,
    error,
    numOfEncounteredErrors,
    emailId,
    honeypotElem,
    handleSubmit,
  } = useNewsletterSignup({
    tags: ['primaryNewsletter'],
    formName: 'primaryNewsletter',
    callbacks: {
      onStart: handleLoadConfetti,
      onSuccess: handleTriggerConfetti,
    },
  });

  return (
    <Wrapper>
      <SubscribeGeyser status={confettiStatus} mode="burst" />

      {status === 'success' ? (
        <SuccessWrapper>
          <Column>
            <Description>
              <Spicy style={{ color: 'var(--color-primary)' }}>
                Success!
              </Spicy>{' '}
              Please check your email. In a moment, you’ll receive a
              welcome message including the{' '}
              <Sparkles colors={['var(--color-sparkle)']}>
                “Josh Starter Pack”
              </Sparkles>
              .
            </Description>
          </Column>
          <JoshWrapper>
            <Josh alt="" src="/email/josh-decapitated.png" />
          </JoshWrapper>
        </SuccessWrapper>
      ) : (
        <>
          <Form onSubmit={handleSubmit}>
            {honeypotElem}

            <Column>
              <EmailLabel htmlFor={emailId}>
                Your email address:
              </EmailLabel>

              <EmailInput id={emailId} {...emailFields} />
            </Column>

            <SubmitButton data-disabled={status === 'submitting'}>
              {status === 'submitting' ? <Spinner /> : 'Subscribe!'}
            </SubmitButton>
          </Form>

          <ErrorMessage
            status={status}
            error={error}
            errorMinHeight="5.28125rem"
            numOfEncounteredErrors={numOfEncounteredErrors}
          />
        </>
      )}
    </Wrapper>
  );
}

const Wrapper = styled.div`
  padding: calc(var(--viewport-padding) * 1);
  margin-inline: calc(var(--viewport-padding) * -1);
  margin-block: 1rem 2rem;
  border-radius: 6px;
  background: var(--syntax-bg);
  transition: background var(--color-swap-duration);
  outline: 1px solid var(--color-content-outline);
  outline-offset: -1px;
`;

const Description = styled(Paragraph)`
  &:last-child {
    margin-bottom: 0;
  }
`;

const Form = styled.form`
  display: flex;
  align-items: flex-end;
  gap: 16px;

  @media (max-width: 25rem) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const Column = styled.div`
  flex: 1;
`;

const EmailLabel = styled.label`
  display: block;
  margin-bottom: 4px;
  font-size: 1.125rem;
  font-weight: var(--font-weight-medium);
`;

const EmailInput = styled(TextInput)`
  display: block;
  width: 100%;
  height: 3rem;
  font-size: 1.125rem;
  outline-offset: 0px;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
const SubmitButton = styled(ButtonRainbow)`
  --poofy-dark: hsl(230deg 50% 24%);
  --poofy-light: hsl(260deg 80% 40%);
  min-width: 9rem;
  min-height: 3rem !important;

  @media (max-width: 25rem) {
    height: 4rem;
    margin-left: -2px;
    margin-right: -2px;
  }
`;

const ErrorMessage = styled(SubscribeError)`
  --color-primary: var(--color-error);
  display: block;
  margin-top: 2.25rem;
  color: var(--color-error);

  p {
    font-size: 1rem;
    text-align: center;
    text-wrap: balance;

    &:not(:last-child) {
      margin-bottom: 0.5em;
    }
  }
`;

const SuccessWrapper = styled.div`
  --color-background-values: ${vals(LIGHT_COLORS_RAW.syntaxBg)};
  --color-primary: oklch(0.55 0.23 146.17);
  --color-sparkle: oklch(0.73 0.32 145.89);

  position: relative;
  /* Make space for the absolutely-positioned mascot: */
  padding-right: 7rem;
  animation: fadeIn 1250ms;

  html[data-color-mode='dark'] & {
    --color-background-values: ${vals(DARK_COLORS_RAW.syntaxBg)};
    --color-primary: oklch(0.88 0.21 92.28);
    --color-sparkle: oklch(0.9 0.21 96.53);
  }

  /*
    We remove mascot on smaller screens, so we should also remove the exaggerated padding.
  */
  @media (max-width: 28rem) {
    padding-right: 0;
  }
`;

const JoshWrapper = styled.div`
  position: absolute;
  right: calc(var(--viewport-padding) * -1 + 4px);
  /* Sit above the content outline in dark mode: */
  bottom: 1px;
  width: 137.5px;
  margin-bottom: calc(var(--viewport-padding) * -1);
  overflow: hidden;
  overflow: clip;
  user-select: none;

  @media (max-width: 28rem) {
    display: none;
  }

  mask-image: linear-gradient(
    to top,
    hsl(0deg 0% 100% / 0) 0%,
    hsl(0deg 0% 100% / 0.013) 2.6px,
    hsl(0deg 0% 100% / 0.049) 5px,
    hsl(0deg 0% 100% / 0.104) 7.2px,
    hsl(0deg 0% 100% / 0.175) 9.3px,
    hsl(0deg 0% 100% / 0.259) 11.2px,
    hsl(0deg 0% 100% / 0.352) 13.1px,
    hsl(0deg 0% 100% / 0.45) 15.1px,
    hsl(0deg 0% 100% / 0.55) 16.9px,
    hsl(0deg 0% 100% / 0.648) 18.8px,
    hsl(0deg 0% 100% / 0.741) 20.7px,
    hsl(0deg 0% 100% / 0.825) 22.7px,
    hsl(0deg 0% 100% / 0.896) 24.8px,
    hsl(0deg 0% 100% / 0.951) 27px,
    hsl(0deg 0% 100% / 0.987) 29.4px,
    hsl(0deg 0% 100%) 32px
  );
`;

const Josh = styled.img`
  display: block;
  width: 100%;
  height: 100%;

  animation:
    fadeIn 400ms 300ms backwards,
    riseIn 800ms 300ms backwards cubic-bezier(0.17, 0.67, 0.49, 0.99);

  @keyframes riseIn {
    from {
      opacity: 0;
      transform: translateY(16px);
    }
  }
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export default ContentNewsletterSignup;
